import { ColoursObj } from "@eventr/common-storybook";
import { NativeNavigation } from "@eventr/native/navigation";
import { useEffect } from "react";
import { useThemeContext } from "../../state/themeState/themeContext";
import { nativeNavigationReact } from "./init";
import { nativeInterfaceStyle, nativeTabBarTint } from "./nativeTheme";

/**
 * Re-applies theme-dependent NATIVE chrome to the already-presented native views when the
 * colour scheme (or custom theme) changes. iOS only; renders nothing.
 *
 * Two things need updating outside the React tree on a theme change:
 *  - the interface style of the liquid-glass toolbar + tab bar (so they render light/dark
 *    to match the theme while keeping their blur, no tint), and
 *  - each open view's html/body background, painted once from ColoursObj at create time
 *    (NativeNavigationViewSetup.lockViewDocument) and otherwise frozen.
 * The web content itself is handled separately by ThemeRemountGate.
 */
export function NativeThemeSync() {
  const scheme = useThemeContext((s) => s.selectedColourScheme);
  const customThemeId = useThemeContext((s) => s.customThemeId);
  // Light -> Device can leave scheme and id unchanged; the style still goes to "system".
  const themeOverwrite = useThemeContext((s) => s.themeOverwrite);

  useEffect(() => {
    // Light/dark the native liquid-glass chrome (toolbar + tab bar) to match the theme,
    // and tint the selected tab item with the theme's primary colour.
    NativeNavigation.setInterfaceStyle({ style: nativeInterfaceStyle() }).catch(() => {});
    NativeNavigation.setTabBarTint({ color: nativeTabBarTint() }).catch(() => {});

    const handle = nativeNavigationReact;
    if (!handle) return;

    // Repaint each open view's document background (mirrors lockViewDocument at create).
    for (const view of Object.values(handle.views())) {
      try {
        const doc = view.window.document;
        doc.documentElement.style.backgroundColor = ColoursObj.backgrounds.primary;
        doc.body.style.backgroundColor = ColoursObj.backgrounds.primary;
      } catch {
        // Cross-window access can throw while a view is tearing down, ignore.
      }
    }
  }, [scheme, customThemeId, themeOverwrite]);

  return null;
}
